/* eslint-disable no-console */
import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { useInput } from "react-admin";

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  place_id: string;
  description: string;
  structured_formatting?: StructuredFormatting;
}

export default function GooglePlacesAutocomplete({
  label
}: {
  label?: string;
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const { field: address_display, fieldState } = useInput({
    source: "address_display"
  });
  const { field: address_longitude } = useInput({
    source: "address_longitude"
  });
  const { field: address_latitude } = useInput({
    source: "address_latitude"
  });
  const { field: google_places_id } = useInput({
    source: "google_places_id"
  });
  const { field: address, fieldState: addressFieldState } = useInput({
    source: "address"
  });
  const { field: city, fieldState: cityFieldState } = useInput({
    source: "city"
  });
  const { field: state, fieldState: stateFieldState } = useInput({
    source: "state"
  });
  const { field: zipcode, fieldState: zipcodeFieldState } = useInput({
    source: "zipcode"
  });
  const { field: country, fieldState: countryFieldState } = useInput({
    source: "country"
  });
  const [value, setValue] = React.useState<PlaceType | null>({
    description: address_display.value,
    place_id: google_places_id.value
  });

  const fetch = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(
            request,
            callback
          );
        },
        200
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <div className="w-full mb-4">
      <Autocomplete
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description
        }
        style={{ width: "100%" }}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        onChange={(_: any, newValue: PlaceType | null) => {
          if (newValue) {
            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode(
              {
                address: newValue?.description
              },
              function (results, status) {
                if (status === google.maps.GeocoderStatus.OK) {
                  address_display.onChange(newValue?.description);
                  google_places_id.onChange(newValue?.place_id);
                  address_latitude.onChange(
                    results?.[0].geometry.location.lat()
                  );
                  address_longitude.onChange(
                    results?.[0].geometry.location.lng()
                  );

                  const result = results?.[0];
                  if (result != null) {
                    let _address: Array<string> = [];
                    let _city: string | null = null;
                    let _state: string | null = null;
                    let _country: string | null = null;
                    let _zipcode: string | null = null;
                    for (var component of result.address_components) {
                      if (component.types.includes("street_number")) {
                        _address.push(component.long_name);
                      } else if (component.types.includes("route")) {
                        _address.push(component.short_name);
                      } else if (component.types.includes("locality")) {
                        _city = component.long_name;
                      } else if (
                        component.types.includes("administrative_area_level_1")
                      ) {
                        _state = component.long_name;
                      } else if (component.types.includes("postal_code")) {
                        _zipcode = component.long_name;
                      } else if (component.types.includes("country")) {
                        _country = component.short_name;
                      }
                    }
                    if (_address.length > 0) {
                      address.onChange(_address.join(" "));
                    }
                    city.onChange(_city);
                    state.onChange(_state);
                    country.onChange(_country);
                    zipcode.onChange(_zipcode);
                  }
                }
              }
            );
          }

          address_display.onChange(null);
          google_places_id.onChange(null);
          address_latitude.onChange(null);
          address_longitude.onChange(null);
          address.onChange(null);
          city.onChange(null);
          state.onChange(null);
          country.onChange(null);
          zipcode.onChange(null);

          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            label={label || "Address"}
            fullWidth
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting &&
            option.structured_formatting.main_text_matched_substrings;
          const parts = parse(
            (option.structured_formatting &&
              option.structured_formatting.main_text) ||
              "",
            matches?.map((match: any) => [
              match.offset,
              match.offset + match.length
            ]) || []
          );

          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item>
                  <Box
                    component={LocationOnIcon}
                    sx={{ color: "text.secondary", mr: 2 }}
                  />
                </Grid>
                <Grid item xs>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting &&
                      option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      {fieldState.error && (
        <span style={{ color: "red", fontSize: "12px" }}>
          {fieldState.error.message}
        </span>
      )}
      {addressFieldState.error && (
        <span style={{ color: "red", fontSize: "12px", display: "block" }}>
          {addressFieldState.error.message}
        </span>
      )}
      {cityFieldState.error && (
        <span style={{ color: "red", fontSize: "12px", display: "block" }}>
          {cityFieldState.error.message}
        </span>
      )}
      {stateFieldState.error && (
        <span style={{ color: "red", fontSize: "12px", display: "block" }}>
          {stateFieldState.error.message}
        </span>
      )}
      {zipcodeFieldState.error && (
        <span style={{ color: "red", fontSize: "12px", display: "block" }}>
          {zipcodeFieldState.error.message}
        </span>
      )}
      {countryFieldState.error && (
        <span style={{ color: "red", fontSize: "12px", display: "block" }}>
          {countryFieldState.error.message}
        </span>
      )}
    </div>
  );
}
